import { ArrowDown, ArrowUp } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Switch } from '@shared/ui/primitives/Switch';
import { env } from '@config/env';
import { useHomeLayoutStore, resolveWidgets } from '@shared/stores/homeLayoutStore';
import { widgetTitle } from './widgets/registry';

/**
 * Lets the member choose which home widgets are shown and in what order.
 * Writes to `homeLayoutStore`; the home page re-renders reactively.
 */
export function CustomizeHomeSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const order = useHomeLayoutStore((s) => s.order);
  const enabledOverride = useHomeLayoutStore((s) => s.enabledOverride);
  const setOrder = useHomeLayoutStore((s) => s.setOrder);
  const setEnabled = useHomeLayoutStore((s) => s.setEnabled);
  const reset = useHomeLayoutStore((s) => s.reset);

  const resolved = resolveWidgets(env.home?.widgets, { order, enabledOverride });

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= resolved.length) return;
    const ids = resolved.map((w) => w.id);
    const a = ids[index];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[j] = a;
    setOrder(ids);
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Customize home"
      description="Choose which widgets show and drag them into the order you want."
    >
      <div className="flex flex-col gap-2 pb-2">
        {resolved.map((w, index) => (
          <div key={w.id} className="flex items-center gap-3 rounded-xl bg-sunken px-3 py-2.5">
            <div className="flex shrink-0 flex-col">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label="Move up"
                className="flex h-5 w-5 items-center justify-center text-ink-secondary disabled:opacity-30"
              >
                <ArrowUp className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === resolved.length - 1}
                aria-label="Move down"
                className="flex h-5 w-5 items-center justify-center text-ink-secondary disabled:opacity-30"
              >
                <ArrowDown className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-sans text-body font-semibold text-ink">{widgetTitle(w)}</p>
              <p className="text-caption capitalize text-ink-muted">{w.type}</p>
            </div>
            <Switch checked={w.enabled} onCheckedChange={(on) => setEnabled(w.id, on)} />
          </div>
        ))}

        {resolved.length === 0 ? (
          <p className="rounded-lg bg-sunken px-4 py-6 text-center text-body-sm text-ink-secondary">
            No widgets are available for your community yet.
          </p>
        ) : (
          <button
            type="button"
            onClick={reset}
            className="mt-2 self-center text-body-sm font-semibold text-primary-700"
          >
            Reset to default
          </button>
        )}
      </div>
    </BottomSheet>
  );
}
